import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { SearchInput } from '@/components/ui/search-input'
import type { EntityType } from '@/services/api/schemas'
import { EntityTypeGlyph } from './EntityTypeGlyph'
import { displayProperty, entityTypeName, humanizeIdentifier } from './entity-form'

interface EntityTypePickerProps {
  types: EntityType[]
  onPick: (type: EntityType) => void
}

function matches(type: EntityType, name: string, query: string): boolean {
  const q = query.trim().toLowerCase()
  if (!q) return true
  return [type.label, name, type.description ?? ''].some(text => text.toLowerCase().includes(q))
}

/** The case schema's entity types, searchable; picking one opens its form. */
export function EntityTypePicker({ types, onPick }: EntityTypePickerProps) {
  const { t, i18n } = useTranslation()
  const [query, setQuery] = useState('')
  const rows = useMemo(
    () =>
      types
        .map(type => ({ type, name: entityTypeName(type, i18n.language) }))
        .filter(({ type, name }) => matches(type, name, query)),
    [types, query, i18n.language]
  )

  return (
    <div className="flex min-w-0 flex-col gap-3">
      <SearchInput
        value={query}
        onValueChange={setQuery}
        placeholder={t('manualGraph.createEntity.searchTypes')}
        aria-label={t('manualGraph.createEntity.searchTypes')}
        autoFocus
      />
      <ul className="-mx-1 max-h-80 min-w-0 overflow-y-auto">
        {rows.map(({ type, name }) => (
          <li key={type.label}>
            <EntityTypeRow type={type} name={name} onPick={onPick} />
          </li>
        ))}
        {rows.length === 0 && (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            {t('manualGraph.createEntity.noTypeMatch', { query })}
          </li>
        )}
      </ul>
    </div>
  )
}

function EntityTypeRow({
  type,
  name,
  onPick,
}: {
  type: EntityType
  name: string
  onPick: (type: EntityType) => void
}) {
  const { t } = useTranslation()
  const namedBy = displayProperty(type)?.name
  const detail =
    type.description?.trim() ||
    (namedBy
      ? t('manualGraph.createEntity.typeNamedBy', { property: humanizeIdentifier(namedBy) })
      : '')
  return (
    <button
      type="button"
      onClick={() => onPick(type)}
      className="flex w-full min-w-0 items-center gap-3 rounded-lg px-2.5 py-2 text-left transition-colors hover:bg-accent/60 focus-visible:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <EntityTypeGlyph label={type.label} />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-sm font-medium text-foreground">{name}</span>
        {detail && <span className="block truncate text-xs text-muted-foreground">{detail}</span>}
      </span>
      <span className="shrink-0 text-xs tabular-nums text-muted-foreground">
        {t('manualGraph.createEntity.typeProperties', { count: type.properties.length })}
      </span>
    </button>
  )
}
